<script setup lang="ts" generic="Row">
import { computed, ref } from 'vue';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/16/solid';
import { useResizableColumns, type ColumnWidths } from '@/composables/useResizableColumns';

export interface SimpleTableColumn {
  key: string;
  label: string;
  /** Initial width in px; columns stay user-resizable from this base. */
  width?: number;
}

/**
 * Lightweight sortable + resizable table for inspector panels and other small
 * surfaces where the store-backed DataTable is too heavy. Sort state is local
 * (click a header to toggle asc/desc); column widths come from
 * useResizableColumns — drag the handle on a header's right edge (or focus it
 * and use arrow keys) to resize, persisted under `storageKey`. Cell content
 * comes from `getValue` (also the sort value) or a `cell` slot for custom
 * rendering.
 */
const props = withDefaults(
  defineProps<{
    columns: SimpleTableColumn[];
    rows: Row[];
    rowKey: (row: Row) => string | number;
    /**
     * localStorage key for persisted column widths. Version it per table so a
     * column-set change can invalidate stored values
     * (e.g. 'live-track-usages:columnWidths:v1').
     */
    storageKey: string;
    /** Sort/display value for a cell; defaults to the row property at column.key. */
    getValue?: (row: Row, columnKey: string) => unknown;
    /** Adds pointer/hover affordances and emits row-click. */
    clickableRows?: boolean;
  }>(),
  { clickableRows: false, getValue: undefined },
);

const emit = defineEmits<{
  rowClick: [row: Row];
}>();

const sortKey = ref<string | null>(null);
const sortDirection = ref<'asc' | 'desc'>('asc');

const defaultWidths: ColumnWidths = Object.fromEntries(
  props.columns.flatMap((column) => (column.width !== undefined ? [[column.key, column.width]] : [])),
);

const { startResize, onHandleKeydown, getColumnStyle } = useResizableColumns({
  storageKey: props.storageKey,
  defaults: defaultWidths,
});

/**
 * Pure DOM read + delegation — no state mutation here, so the composable's
 * primary-button guard stays the single authority. Auto-width columns seed
 * their first drag from the measured header width (0 in jsdom → undefined →
 * composable fallback chain).
 */
function onResizePointerDown(columnKey: string, event: PointerEvent): void {
  const measuredWidth =
    (event.currentTarget as HTMLElement).closest('th')?.getBoundingClientRect().width || undefined;
  startResize(columnKey, event, measuredWidth);
}

function cellValue(row: Row, columnKey: string): unknown {
  return props.getValue
    ? props.getValue(row, columnKey)
    : (row as Record<string, unknown>)[columnKey];
}

function toggleSort(columnKey: string): void {
  if (sortKey.value === columnKey) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    return;
  }
  sortKey.value = columnKey;
  sortDirection.value = 'asc';
}

const sortedRows = computed(() => {
  const key = sortKey.value;
  if (!key) return props.rows;

  const direction = sortDirection.value === 'asc' ? 1 : -1;
  return [...props.rows].sort((a, b) => {
    const left = cellValue(a, key);
    const right = cellValue(b, key);
    // Empty values sort last regardless of direction.
    const leftEmpty = left === null || left === undefined || left === '';
    const rightEmpty = right === null || right === undefined || right === '';
    if (leftEmpty || rightEmpty) return Number(leftEmpty) - Number(rightEmpty);
    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * direction;
    }
    return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' }) * direction;
  });
});

</script>

<template>
  <table class="w-full table-fixed text-left text-sm">
    <colgroup>
      <col v-for="column in columns" :key="column.key" :style="getColumnStyle(column.key)" />
    </colgroup>
    <thead>
      <tr class="text-muted-foreground border-b text-xs uppercase">
        <th
          v-for="column in columns"
          :key="column.key"
          class="relative py-1.5 pr-2 font-medium select-none"
        >
          <button
            type="button"
            class="hover:text-foreground inline-flex max-w-full cursor-pointer items-center gap-1 uppercase"
            :data-testid="`sort-${column.key}`"
            @click="toggleSort(column.key)"
          >
            <span class="truncate">{{ column.label }}</span>
            <ChevronUpIcon v-if="sortKey === column.key && sortDirection === 'asc'" class="h-3 w-3 shrink-0" />
            <ChevronDownIcon v-else-if="sortKey === column.key" class="h-3 w-3 shrink-0" />
          </button>
          <span
            role="separator"
            aria-orientation="vertical"
            :aria-label="`Resize ${column.label} column`"
            tabindex="0"
            class="absolute top-0 right-0 h-full w-1.5 cursor-col-resize touch-none select-none"
            :data-testid="`resize-${column.key}`"
            @pointerdown="onResizePointerDown(column.key, $event)"
            @keydown="onHandleKeydown(column.key, $event)"
          />
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="row in sortedRows"
        :key="rowKey(row)"
        class="border-b last:border-b-0"
        :class="clickableRows ? 'hover:bg-muted/60 cursor-pointer' : ''"
        :data-testid="`simple-table-row-${rowKey(row)}`"
        @click="clickableRows && emit('rowClick', row)"
      >
        <td v-for="column in columns" :key="column.key" class="truncate py-1.5 pr-2">
          <slot name="cell" :row="row" :column="column">
            {{ cellValue(row, column.key) ?? '—' }}
          </slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>
